import { withStillCorsCacheKey } from '@/lib/cover/withStillCorsCacheKey';
import { EXPORT_FPS } from '@/lib/timeline/exportFrameTiming';

/** The Remotion render the worker runs for a clip (`processRenderV2` render options). */
export interface StudioRenderRequest {
  composition: string;
  width: number;
  height: number;
  fps: number;
  durationInFrames: number;
  inputProps: { project: Record<string, unknown> };
}

interface RenderInterval {
  finalEndFrame: number;
}

interface SavedRenderData {
  composition?: string;
  maxOutputDimension?: number;
  crop?: { aspectRatio?: number };
  data?: {
    clipIntervals?: RenderInterval[];
    overlays?: unknown;
    transitions?: unknown;
    keyframeTransitions?: unknown;
    videoEffects?: unknown;
  };
  [key: string]: unknown;
}

/** Historic paid long-side cap (1080p), like the worker's DEFAULT_MAX_OUTPUT_DIMENSION. */
const DEFAULT_MAX_OUTPUT_DIMENSION = 1920;

/**
 * Overlays end with the clip, as Remotion clips them to the composition: the backend stamps
 * watermarks and logos with `end: 3600` ("until the end"), which FreeCut would import as an
 * hour-long track.
 */
const endOverlaysWithClip = (overlays: unknown, durationSec: number): unknown => {
  if (!overlays || typeof overlays !== 'object') return overlays;
  const capped: Record<string, unknown> = { ...(overlays as Record<string, unknown>) };
  for (const side of ['front', 'back']) {
    const items = capped[side];
    if (!Array.isArray(items)) continue;
    capped[side] = items.map((item) => {
      const end = (item as { end?: unknown } | null)?.end;
      return typeof end === 'number' && end > durationSec ? { ...item, end: durationSec } : item;
    });
  }
  return capped;
};

/** Remotion compositions must be even on both axes. */
const even = (size: number): number => {
  const round = Math.round(size);
  return round % 2 === 1 ? Math.max(round - 1, 2) : round;
};

/**
 * The worker's composition size (`resolveCompositionSize`): the plan's long-side cap along the
 * crop aspect.
 */
const compositionSize = (aspectRatio: number, maxDimension: number): { width: number; height: number } =>
  aspectRatio > 1
    ? { width: maxDimension, height: even(maxDimension / aspectRatio) }
    : { width: even(maxDimension * aspectRatio), height: maxDimension };

/**
 * The Remotion render request for a clip's render data (`myClipRenderData`: what an export of
 * the clip would hand the worker), exactly as `processRenderV2` hands it to Remotion — the input
 * FreeCut Studio imports. Null without scenes to render.
 */
export function studioRenderRequest(renderData: unknown): StudioRenderRequest | null {
  const render = renderData as SavedRenderData | null | undefined;
  const intervals = render?.data?.clipIntervals;
  if (!render || !Array.isArray(intervals) || intervals.length === 0) return null;
  const stamped = Number(render.maxOutputDimension);
  const maxDimension = Number.isFinite(stamped) && stamped > 0 ? stamped : DEFAULT_MAX_OUTPUT_DIMENSION;
  const { width, height } = compositionSize(render.crop?.aspectRatio ?? 9 / 16, maxDimension);
  const { data } = render;
  const durationInFrames = Math.max(...intervals.map((interval) => interval.finalEndFrame));
  return {
    composition: render.composition || 'Main',
    width,
    height,
    fps: EXPORT_FPS,
    durationInFrames,
    inputProps: {
      // FreeCut fetches the media in CORS mode; clip cards may have cached the same storage URLs
      // without CORS headers, so they get their own cache entry.
      project: withStillCorsCacheKey({
        ...render,
        data: {
          clipIntervals: data?.clipIntervals,
          overlays: endOverlaysWithClip(data?.overlays, durationInFrames / EXPORT_FPS),
          transitions: data?.transitions,
          keyframeTransitions: data?.keyframeTransitions,
          videoEffects: data?.videoEffects,
        },
      }),
    },
  };
}
